// frontend/src/services/progressiveResults.js
// Service for progressive results API calls

import { getApiBaseUrl } from "../config/api.js";

const API_BASE_URL = getApiBaseUrl();

/**
 * Generates JWT Authentication headers from stored JWT token.
 * @returns {Object} An object containing the Authorization and Content-Type headers.
 */
const getJwtHeaders = () => {
  const token = localStorage.getItem("jwtToken");
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }
  return {};
};

/**
 * DEPRECATED: Basic Auth with stored password is no longer supported for security reasons.
 * @returns {Object} Always returns empty object.
 */
const getAuthHeaders = () => {
  return {};
};

/**
 * Gets authentication headers using JWT only (password storage removed for security).
 * @returns {Object} Authentication headers object with JWT token if available.
 */
const getAuthHeadersWithJwt = () => {
  // Use JWT only (no password storage)
  return getJwtHeaders();
};

/**
 * Trigger scoping with progressive results support
 * @param {Object} options - Scoping options
 * @param {Array} options.sections - Sections to scope (default: ['all'])
 * @param {number} options.max_pages - Max pages per collection (default: 4)
 * @param {boolean} options.force_refresh - Force refresh cache (default: false)
 * @returns {Promise<Object>} Response with session_id for progress tracking
 */
export const triggerScopingWithProgress = async (options = {}) => {
  const {
    sections = ["all"],
    max_pages = 4,
    force_refresh = false,
    enable_deep_scoping = false,
    deep_scoping_save_to_db = true,
    deep_scoping_max_products = 0,
  } = options;

  const response = await fetch(`${API_BASE_URL}/api/scope`, {
    method: "POST",
    headers: getAuthHeadersWithJwt(),
    body: JSON.stringify({
      sections,
      max_pages,
      force_refresh,
      enable_deep_scoping,
      deep_scoping_save_to_db,
      deep_scoping_max_products,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Scoping request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get real-time progress for a scoping session
 * @param {string} sessionId - Session ID to get progress for
 * @param {number} retryCount - Current retry count (default: 0)
 * @param {number} maxRetries - Maximum number of retries (default: 10)
 * @returns {Promise<Object>} Current progress information
 */
export const getScopingProgress = async (
  sessionId,
  retryCount = 0,
  maxRetries = 10
) => {
  const response = await fetch(
    `${API_BASE_URL}/api/scope/progress/${sessionId}`,
    {
      method: "GET",
      headers: getAuthHeadersWithJwt(),
    }
  );

  if (!response.ok) {
    if (response.status === 404) {
      if (retryCount >= maxRetries) {
        throw new Error(
          `Session ${sessionId} not found after ${maxRetries} retries`
        );
      }
      // Retry after a short delay
      await new Promise((resolve) => setTimeout(resolve, 2000));
      return getScopingProgress(sessionId, retryCount + 1, maxRetries);
    }
    throw new Error(
      `Progress request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get all active scoping sessions
 * @returns {Promise<Object>} List of active sessions
 */
export const getActiveSessions = async () => {
  const response = await fetch(`${API_BASE_URL}/api/scope/sessions`, {
    method: "GET",
    headers: getAuthHeadersWithJwt(),
  });

  if (!response.ok) {
    throw new Error(
      `Active sessions request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Clean up completed sessions
 * @param {number} maxAgeHours - Maximum age in hours for sessions to keep (default: 24)
 * @returns {Promise<Object>} Cleanup results
 */
export const cleanupSessions = async (maxAgeHours = 24) => {
  const response = await fetch(`${API_BASE_URL}/api/scope/cleanup-sessions`, {
    method: "POST",
    headers: getAuthHeadersWithJwt(),
    body: JSON.stringify({
      max_age_hours: maxAgeHours,
    }),
  });

  if (!response.ok) {
    throw new Error(
      `Cleanup request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get cache status for all collections
 * @returns {Promise<Object>} Cache status information
 */
export const getCacheStatus = async () => {
  const response = await fetch(`${API_BASE_URL}/api/scope/cache-status`, {
    method: "GET",
    headers: getAuthHeadersWithJwt(),
  });

  if (!response.ok) {
    throw new Error(
      `Cache status request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get timeout status and statistics
 * @returns {Promise<Object>} Timeout status information
 */
export const getTimeoutStatus = async () => {
  const response = await fetch(`${API_BASE_URL}/api/scope/timeout-status`, {
    method: "GET",
    headers: getAuthHeadersWithJwt(),
  });

  if (!response.ok) {
    throw new Error(
      `Timeout status request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get timeout history for a specific collection
 * @param {string} collectionName - Name of the collection
 * @returns {Promise<Object>} Timeout history for the collection
 */
export const getCollectionTimeoutHistory = async (collectionName) => {
  const response = await fetch(
    `${API_BASE_URL}/api/scope/timeout-history/${collectionName}`,
    {
      method: "GET",
      headers: getAuthHeadersWithJwt(),
    }
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(
        `No timeout data found for collection: ${collectionName}`
      );
    }
    throw new Error(
      `Timeout history request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Stop a running scoping session
 * @param {string} sessionId - Session ID to stop
 * @returns {Promise<Object>} Stop result
 */
export const stopScopingSession = async (sessionId) => {
  const response = await fetch(
    `${API_BASE_URL}/api/scope/sessions/${sessionId}/stop`,
    {
      method: "POST",
      headers: getAuthHeadersWithJwt(),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Stop session request failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};
